import { h, Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import ChevronDown from '../utils/icon/chevron-down.svg';
import CountrySelectorModal from './country-selector-modal';
import extractURLParams from '../utils/utm';

const modalsContainer = document.getElementById('modals-container');

const CountrySelector = ({
  countries,
  languages,
  locale,
  message,
  hreflangUrl,
  countryISO,
  hideCloseBtnSetting,
  autoRedirect: defaultAutoRedirect,
  canadianStoreUrl,
  internationalStoreUrl,
  hidePopup,
}) => {
  const autoRedirect = defaultAutoRedirect !== false;

  const country = useMemo(
    () => countries.find((i) => i.iso_code.find((code) => code.toLowerCase().trim() === countryISO?.toLowerCase())),
    [countries, countryISO],
  );
  const language = useMemo(
    () => languages.find((i) => i.iso_code.toLowerCase() === locale.toLowerCase()),
    [languages, locale],
  );

  const [isModalActive, setIsModalActive] = useState(false);
  const [userLocation, setUserLocation] = useState({});

  const container = useRef(null);
  const showModal = () => {
    document.body.classList.add('no-scroll');
    setIsModalActive(true);
  };
  const hideModal = () => {
    document.body.classList.remove('no-scroll');
    setIsModalActive(false);
  };

  const getCountry = async () => {
    const params = new URLSearchParams(window.location.search);
    const shopifyLocationResponse = await fetch(
      // eslint-disable-next-line max-len
      `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true`,
    ).then((r) => r.json());

    const geoData = {
      country: shopifyLocationResponse?.detected_values?.country?.handle,
      language: shopifyLocationResponse?.suggestions?.[0]?.parts?.language?.handle,
    };

    setUserLocation(geoData);
    let expire = new Date(Date.now() + 10 * 864e5).toUTCString();
    // eslint-disable-next-line max-len
    document.cookie = `geo_data=${JSON.stringify(geoData)}; expires=${expire}; path=/`;

    if (params.has('geo')) {
      expire = new Date(Date.now() + 300 * 864e5).toUTCString();
      document.cookie = `geo_selected=1; expires=${expire}; path=/`;
    } else if (!autoRedirect) {
      if (hidePopup === 'false') showModal();
    }
  };

  useEffect(() => {
    if (Object.keys(userLocation).length === 0) return;
    if (/dev/.test(window.location.href)) return;

    const params = new URLSearchParams(window.location.search);
    const isCountrySelected = document.cookie.match('(^|; )force_geo=([^;]*)') || 0;

    const storeCountry = Shopify.country;
    const userCountry = userLocation.country;
    const geoParam = {
      geo: 1,
    };

    const utmParam = params.toString().includes('utm') ? extractURLParams(window.location.href) : {};

    const finalParams = {
      ...utmParam,
      ...geoParam,
    };

    const geoString = `?${new URLSearchParams(finalParams).toString()}`;

    if (!autoRedirect || params.has('geo') || Shopify.designMode) return;

    if (storeCountry !== userCountry && !isCountrySelected) {
      // set cookie to prevent redirecting twice.
      const expire = new Date(Date.now() + 10 * 864e5).toUTCString();
      document.cookie = `force_geo=1; expires=${expire}; path=/`;

      // redirect to the country detected
      if (userCountry === 'CA') {
        // redirect to the canadian store
        const canadaStore = canadianStoreUrl || 'https://saje.ca';
        window.location.href = canadaStore + geoString;
      } else {
        // redirect to the US store
        const internationalStore = internationalStoreUrl || 'https://saje.com';
        window.location.href = internationalStore + geoString;
      }
    }
  }, [userLocation]);

  useEffect(() => {
    document.querySelectorAll('[data-country-selector]').forEach((element) => {
      element.addEventListener('click', showModal);
    });
    const geoData = (document.cookie.match('(^|; )geo_data=([^;]*)') || 0)[2];
    if (!geoData) {
      getCountry();
    } else {
      setUserLocation(JSON.parse(geoData));
    }
    return () => {
      document.querySelectorAll('[data-country-selector]').forEach((element) => {
        element.removeEventListener('click', showModal);
      });
    };
  }, []);

  useEffect(() => {
    if (container.current) {
      document.querySelectorAll('[data-country-selector]').forEach((element) => {
        // eslint-disable-next-line no-param-reassign
        element.innerHTML = container.current.outerHTML;
      });
    }
  }, [country]);

  if (!country) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2 cursor-pointer" ref={container}>
        <img height="17" width="23" src={country.flag_icon} alt={country.iso_code} className="w-auto h-[17px] hidden-on-mobile-nav-b-test" />
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label={country.iso_code} className="hidden mobile-nav-international-icon">
          <path d="M8 1.84961C10.6101 1.84961 12.8441 3.4862 13.7354 5.78516C13.7386 5.79222 13.7417 5.79776 13.7432 5.80176C13.7442 5.80459 13.7444 5.80747 13.7451 5.80957L13.8369 6.06738C14.0392 6.67525 14.1504 7.32464 14.1504 8C14.1504 8.77185 14.0059 9.50783 13.7451 10.1885L13.7432 10.1875C13.7408 10.1951 13.738 10.2026 13.7344 10.2109L13.7363 10.2119C12.8458 12.5123 10.6112 14.1504 8 14.1504C5.38686 14.1504 3.15212 12.5125 2.26367 10.21V10.209C2.2599 10.2006 2.25645 10.194 2.25488 10.1895V10.1904C1.99413 9.50976 1.84961 8.77175 1.84961 8C1.84961 7.23014 1.99422 6.49206 2.25293 5.81348H2.25391C2.25741 5.80261 2.26046 5.79318 2.26367 5.78613L2.35059 5.57227C3.2937 3.38473 5.47044 1.84961 8 1.84961ZM6.02148 10.4258C6.20972 11.3013 6.49196 12.0444 6.83105 12.5742C7.19679 13.1456 7.60372 13.4365 8 13.4365C8.39628 13.4365 8.80321 13.1456 9.16895 12.5742C9.50804 12.0444 9.79028 11.3013 9.97852 10.4258H6.02148ZM3.13867 10.4258C3.79623 11.739 4.9683 12.7474 6.38867 13.1885C5.89221 12.5271 5.51167 11.5635 5.28613 10.4258H3.13867ZM10.7119 10.4258C10.4877 11.5641 10.1063 12.5266 9.60938 13.1885C11.0314 12.7474 12.2034 11.739 12.8594 10.4258H10.7119ZM10.8311 6.28809C10.902 6.82898 10.9434 7.40253 10.9434 8C10.9434 8.59705 10.9033 9.17057 10.8311 9.71191H13.1572C13.3369 9.17279 13.4365 8.59914 13.4365 8C13.4365 7.40086 13.3369 6.82721 13.1572 6.28809H10.8311ZM5.89355 6.28809C5.81419 6.82907 5.77051 7.40451 5.77051 8C5.77051 8.59507 5.81649 9.17046 5.89453 9.71191H10.1064C10.1858 9.17093 10.2295 8.59549 10.2295 8C10.2295 7.4047 10.1861 6.82923 10.1084 6.28809H5.89355ZM2.84277 6.28809C2.66315 6.82721 2.56348 7.40086 2.56348 8C2.56348 8.59914 2.66315 9.17279 2.84277 9.71191H5.16895C5.09799 9.17102 5.05664 8.59747 5.05664 8C5.05664 7.40295 5.09672 6.82943 5.16895 6.28809H2.84277ZM9.61035 2.81055C10.1072 3.47194 10.4882 4.43584 10.7139 5.57422H12.8613C12.2036 4.26063 11.0313 3.25146 9.61035 2.81055ZM8 2.56348C7.60372 2.56348 7.19679 2.85445 6.83105 3.42578C6.49196 3.95557 6.20972 4.69874 6.02148 5.57422H9.97852C9.79028 4.69874 9.50804 3.95557 9.16895 3.42578C8.80321 2.85445 8.39628 2.56348 8 2.56348ZM6.38965 2.81055C4.9679 3.25169 3.79619 4.26108 3.13867 5.57422H5.28613C5.5118 4.43623 5.89271 3.47185 6.38965 2.81055Z" fill="#402518" stroke="#402518" strokeWidth="0.3"/>
        </svg>
        <span className="whitespace-nowrap overflow-hidden text-ellipsis text-body-2">
          {country.name} | {locale.toUpperCase()}
        </span>
        <ChevronDown />
      </div>

      {isModalActive &&
        createPortal(
          <CountrySelectorModal
            countries={countries}
            languages={languages}
            message={message}
            hreflangUrl={hreflangUrl}
            defaultCountry={country}
            defaultLanguage={language}
            geoLocation={userLocation}
            onClose={hideModal}
            hideCloseBtnSetting={hideCloseBtnSetting}
            autoRedirect={autoRedirect}
          />,
          modalsContainer,
        )}
    </>
  );
};

export default CountrySelector;
